/**
 * Tiny dotenv parser for the bulk-import flow.
 *
 * Accepts the common .env shape: KEY=VALUE per line, # comments, optional
 * `export` prefix, single- and double-quoted values. Double-quoted values
 * may span multiple lines (covers PEM keys, which are the main reason anyone
 * needs multiline in a paste). Escape sequences \n \r \t \\ \" \` are
 * interpreted inside double quotes; single quotes are literal.
 *
 * Output is a flat list of rows in source order, including parse-error rows
 * so the UI can surface them inline with their original line numbers.
 * Duplicate keys are flagged on the second-and-later occurrence — the first
 * keeps `kind: "secret"`, the rest get `kind: "error"` so the user can fix
 * them before submitting.
 *
 * This is intentionally a value-only parser. It does not validate that the
 * key looks like a path or that the value fits the size budget; the import
 * route layers those checks on top.
 */

export type ParsedRow =
  | {
      kind: "secret";
      lineNumber: number;
      key: string;
      value: string;
      contentType: string;
    }
  | {
      kind: "error";
      lineNumber: number;
      key: string | null;
      raw: string;
      error: string;
    };

const KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function parseDotenv(input: string): ParsedRow[] {
  const rows: ParsedRow[] = [];
  const seenKeys = new Set<string>();
  const lines = input.split(/\r?\n/);

  let i = 0;
  while (i < lines.length) {
    const lineNumber = i + 1;
    const rawLine = lines[i] ?? "";
    const trimmed = rawLine.trim();
    i += 1;

    // Skip blank lines and full-line comments.
    if (trimmed.length === 0 || trimmed.startsWith("#")) continue;

    // Strip an optional `export ` prefix. Tolerates `export ` (one space) only —
    // tabs would be unusual; we surface a parse error if the line ends up
    // looking malformed anyway.
    const body = trimmed.startsWith("export ")
      ? trimmed.slice("export ".length).trimStart()
      : trimmed;

    const eqIdx = body.indexOf("=");
    if (eqIdx === -1) {
      rows.push({
        kind: "error",
        lineNumber,
        key: null,
        raw: rawLine,
        error: "Line is missing '=' — expected KEY=VALUE.",
      });
      continue;
    }

    const key = body.slice(0, eqIdx).trim();
    const afterEq = body.slice(eqIdx + 1);

    if (!KEY_RE.test(key)) {
      rows.push({
        kind: "error",
        lineNumber,
        key: key || null,
        raw: rawLine,
        error: key
          ? `Invalid key "${key}" — env var names must match [A-Za-z_][A-Za-z0-9_]*.`
          : "Missing key before '='.",
      });
      continue;
    }

    // Distinguish quoted vs unquoted values. For quoted values we may need to
    // consume additional lines (multiline double-quoted strings).
    const firstChar = afterEq.trimStart()[0];
    let value: string;
    let consumedExtra = 0;

    if (firstChar === '"' || firstChar === "'") {
      // The opening quote can have leading whitespace after '='; preserve only
      // characters between the matching quotes.
      const quoteChar = firstChar;
      const result = readQuotedValue(lines, i - 1, quoteChar);
      if (result.kind === "error") {
        rows.push({
          kind: "error",
          lineNumber,
          key,
          raw: rawLine,
          error: result.error,
        });
        // Skip past any lines we already scanned so we don't double-report.
        i += result.linesConsumed;
        continue;
      }
      value = result.value;
      consumedExtra = result.linesConsumed;
    } else {
      // Unquoted: strip a trailing inline comment ("foo # note") and trim
      // surrounding whitespace. We only treat ' #' as a comment start, never
      // a bare '#' at the start of a substring like 'pa#ss'.
      const noComment = stripInlineComment(afterEq);
      value = noComment.trim();
    }

    if (seenKeys.has(key)) {
      rows.push({
        kind: "error",
        lineNumber,
        key,
        raw: rawLine,
        error: `Duplicate key "${key}" — earlier occurrence already imported.`,
      });
    } else {
      seenKeys.add(key);
      rows.push({
        kind: "secret",
        lineNumber,
        key,
        value,
        contentType: detectContentType(value),
      });
    }

    i += consumedExtra;
  }

  return rows;
}

type ReadQuotedResult =
  | { kind: "ok"; value: string; linesConsumed: number }
  | { kind: "error"; error: string; linesConsumed: number };

// Scan from the first occurrence of `quoteChar` on `startLineIdx` and collect
// characters up to the matching closing quote, crossing line boundaries if
// needed. The caller has already confirmed the quote exists on the start line.
function readQuotedValue(
  lines: string[],
  startLineIdx: number,
  quoteChar: '"' | "'",
): ReadQuotedResult {
  const firstRaw = lines[startLineIdx] ?? "";
  const firstQuoteIdx = firstRaw.indexOf(quoteChar);
  if (firstQuoteIdx === -1) {
    return {
      kind: "error",
      error: "Internal parse error (missing opening quote).",
      linesConsumed: 0,
    };
  }

  let out = "";
  let lineIdx = startLineIdx;
  let pos = firstQuoteIdx + 1;
  let linesConsumed = 0;
  // Double-quoted strings expand escapes; single-quoted strings are literal.
  const expandEscapes = quoteChar === '"';

  while (lineIdx < lines.length) {
    const line = lines[lineIdx] ?? "";
    while (pos < line.length) {
      const ch = line[pos];
      if (ch === "\\" && expandEscapes && pos + 1 < line.length) {
        const next = line[pos + 1];
        switch (next) {
          case "n":
            out += "\n";
            break;
          case "r":
            out += "\r";
            break;
          case "t":
            out += "\t";
            break;
          case "\\":
            out += "\\";
            break;
          case '"':
            out += '"';
            break;
          case "`":
            out += "`";
            break;
          default:
            // Unknown escape — keep the backslash so the user sees it round-trip.
            out += `\\${next}`;
        }
        pos += 2;
        continue;
      }
      if (ch === quoteChar) {
        // Closed cleanly. Anything after the closing quote on the same line is
        // allowed only if it's whitespace or an inline comment.
        const rest = line.slice(pos + 1).trim();
        if (rest.length > 0 && !rest.startsWith("#")) {
          return {
            kind: "error",
            error: `Unexpected characters after closing ${quoteChar === '"' ? "double" : "single"} quote.`,
            linesConsumed,
          };
        }
        return { kind: "ok", value: out, linesConsumed };
      }
      out += ch;
      pos += 1;
    }
    // End of line without a closing quote — for double quotes we continue on
    // the next line (with a literal '\n'); single quotes also wrap, matching
    // dotenv-expand semantics. Both branches advance the same way.
    out += "\n";
    lineIdx += 1;
    linesConsumed += 1;
    pos = 0;
  }

  return {
    kind: "error",
    error: `Unterminated ${quoteChar === '"' ? "double" : "single"}-quoted value.`,
    linesConsumed,
  };
}

function stripInlineComment(s: string): string {
  // Match the dotenv convention: a '#' preceded by whitespace starts a comment.
  // A '#' inside a token (e.g. `pa#ss`) is part of the value.
  let inSingle = false;
  let inDouble = false;
  for (let j = 0; j < s.length; j += 1) {
    const ch = s[j];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (ch === "#" && !inSingle && !inDouble) {
      const prev = j > 0 ? s[j - 1] : " ";
      if (prev === " " || prev === "\t") return s.slice(0, j);
    }
  }
  return s;
}

export function detectContentType(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length < 2) return "text/plain";
  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  const looksJson = (first === "{" && last === "}") || (first === "[" && last === "]");
  if (!looksJson) return "text/plain";
  try {
    JSON.parse(trimmed);
    return "application/json";
  } catch {
    return "text/plain";
  }
}
